import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import Stack from 'components/core/Stack';
import Input from 'components/core/Input';
import Select from 'components/core/Select';
import Button from 'components/core/Button';
import Typography from 'components/core/Typography';
import Grid from 'components/core/Grid';
import IconButton from 'components/core/IconButton';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import DeleteIcon from '@mui/icons-material/Delete';
import LookupTableInput from 'components/LookupTableInput';

import taxonomyActions from 'actions/taxonomyActions';
import taxonomyNameActions from 'actions/taxonomyNameActions';
import alternateIdentifierActions from 'actions/alternateIdentifierActions';

import Taxonomy from 'domain/Taxonomy';

const TaxonomyForm = ({ options, onSubmit, taxonomy: initialTaxonomy, taxonomies, initialValues }) => {
    const dispatch = useDispatch();
    
    const [taxonomy, setTaxonomy] = useState(() => {
        if (initialValues?.taxonomy) {
            return initialValues.taxonomy;
        } else if (initialTaxonomy) {
            return initialTaxonomy;
        } else {
            return new Taxonomy();
        }
    });
    
    const [names, setNames] = useState(() => {
        if (initialValues?.names && initialValues.names.length > 0) {
            return initialValues.names.map(name => ({
                id: name.id,
                typeId: name.get('typeId'),
                name: name.get('name'),
                _isExisting: true
            }));
        } else {
            return [{ typeId: 'scientific', name: '', _isExisting: false }];
        }
    });
    
    const [errors, setErrors] = useState({});
    
    const [primaryName, setPrimaryName] = useState(0);
    const [primaryIdentifier, setPrimaryIdentifier] = useState(() => {
        if (initialValues?.alternateIdentifiers && initialValues.alternateIdentifiers.length > 0) {
            const primaryIndex = initialValues.alternateIdentifiers.findIndex(id => id.get('primary'));
            return primaryIndex !== -1 ? primaryIndex : 0;
        }
        return 0;
    });
    
    const [alternateIdentifiers, setAlternateIdentifiers] = useState(() => {
        if (initialValues?.alternateIdentifiers && initialValues.alternateIdentifiers.length > 0) {
            return initialValues.alternateIdentifiers.map(identifier => ({
                id: identifier.id,
                taxonomySystem: identifier.get('system'),
                identifier: identifier.get('identifier'),
                primary: identifier.get('primary') || false,
                _isExisting: true
            }));
        } else {
            return [];
        }
    });
    
    const [deletedNameIds, setDeletedNameIds] = useState([]);
    const [deletedIdentifierIds, setDeletedIdentifierIds] = useState([]);

    useEffect(() => {
        if (initialValues?.taxonomy) {
            setTaxonomy(initialValues.taxonomy);
        }
    }, [initialValues]);

    const mapTypeIdFromDB = (dbTypeId) => {
        switch (dbTypeId) {
            case 'SCI': return 'scientific';
            case 'COM': return 'common';
            case 'SYN': return 'scientific';
            default: return 'scientific';
        }
    };

    const mapTypeIdToDB = (formTypeId) => {
        switch (formTypeId) {
            case 'scientific': return 'SCI';
            case 'common': return 'COM';
            default: return 'SCI';
        }
    };

    useEffect(() => {
        if (initialValues?.names && initialValues.names.length > 0) {
            const formattedNames = initialValues.names.map(name => {
                const mappedTypeId = mapTypeIdFromDB(name.get('typeId'));
                return {
                    id: name.id,
                    typeId: mappedTypeId,
                    name: name.get('name'),
                    _isExisting: true
                };
            });
            setNames(formattedNames);
        }
    }, [initialValues]);

    useEffect(() => {
        if (initialValues?.alternateIdentifiers && initialValues.alternateIdentifiers.length > 0) {
            const formattedIdentifiers = initialValues.alternateIdentifiers.map(identifier => ({
                id: identifier.id,
                taxonomySystem: identifier.get('system'),
                identifier: identifier.get('identifier'),
                _isExisting: true
            }));
            setAlternateIdentifiers(formattedIdentifiers);
        }
    }, [initialValues]);

    useEffect(() => {
        if (initialValues?.taxonomy && names.length > 0) {
            const primaryNameText = initialValues.taxonomy.get('primaryName');
            const primaryIndex = names.findIndex(name => name.name === primaryNameText);
            if (primaryIndex >= 0) {
                setPrimaryName(primaryIndex);
            }
        }
    }, [initialValues, names]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});

        try {
            const primaryNameData = names[primaryName];
            const isEditMode = initialValues?.taxonomy?.id;

            const existingTaxonomy = await dispatch(
                taxonomyActions.checkPrimaryNameExists(
                    primaryNameData.name, 
                    isEditMode ? initialValues.taxonomy.id : null
                )
            );
            
            if (existingTaxonomy.value) {
                setErrors({ submit: 'This taxonomy already exists.' });
                return;
            }

            const cleanAlternateIdentifiers = alternateIdentifiers.filter(identifier => identifier.identifier && identifier.taxonomySystem);

            const taxonomyData = {
                objectId: isEditMode ? initialValues.taxonomy.id : null,
                subject: taxonomy.get('subject')?.id,
                reproduction: taxonomy.get('reproduction')?.id,
                nativityType: taxonomy.get('nativityType')?.id,
                invasiveStatus: taxonomy.get('invasiveStatus')?.id,
                securityType: taxonomy.get('securityType')?.id,
                protectedStatus: taxonomy.get('protectedStatus')?.id,
                primarySystem: taxonomy.get('primarySystem')?.id,
                parent: taxonomy.get('parent')?.id,
                taxonomyType: taxonomy.get('taxonomyType')?.id,
                taxonomyCategory: taxonomy.get('taxonomyCategory')?.id,
                sRank: taxonomy.get('sRank')?.id,
                rigcn: taxonomy.get('rigcn')?.id,
                rinhsStatus: taxonomy.get('rinhsStatus')?.id,
                primaryName: primaryNameData.name,
                alternateIdentifiers: cleanAlternateIdentifiers
            };

            const savedTaxonomy = await dispatch(taxonomyActions.save(taxonomyData));
            const taxonomyId = isEditMode ? initialValues.taxonomy.id : savedTaxonomy.value.id;

            if (isEditMode) {
                if (deletedNameIds.length > 0) {
                    await dispatch(taxonomyNameActions.deleteNames(deletedNameIds));
                }
                const namePromises = names.map(name => {
                    const nameData = {
                        objectId: name._isExisting ? name.id : null,
                        typeId: mapTypeIdToDB(name.typeId),
                        name: name.name,
                        taxonomy: taxonomyId
                    };
                    return dispatch(taxonomyNameActions.save(nameData));
                });
                
                await Promise.all(namePromises);
            } else {
                const namePromises = names.map(name => {
                    const nameData = {
                        typeId: mapTypeIdToDB(name.typeId),
                        name: name.name,
                        taxonomy: taxonomyId
                    };
                    return dispatch(taxonomyNameActions.save(nameData));
                });
                
                await Promise.all(namePromises);
            }

            if (isEditMode) {
                if (deletedIdentifierIds.length > 0) {
                    await dispatch(alternateIdentifierActions.deleteIdentifiers(deletedIdentifierIds));
                }
                const identifierPromises = cleanAlternateIdentifiers.map(identifier => {
                    const identifierData = {
                        objectId: identifier._isExisting ? identifier.id : null,
                        taxonomy: taxonomyId,
                        system: identifier.taxonomySystem.id,
                        identifier: identifier.identifier,
                        primary: identifier.primary || false
                    };
                    return dispatch(alternateIdentifierActions.save(identifierData));
                });
                
                await Promise.all(identifierPromises);
            } else {
                // Handle create mode - save alternate identifiers for new taxonomy
                const identifierPromises = cleanAlternateIdentifiers.map(identifier => {
                    const identifierData = {
                        taxonomy: taxonomyId,
                        system: identifier.taxonomySystem.id,
                        identifier: identifier.identifier,
                        primary: identifier.primary || false
                    };
                    return dispatch(alternateIdentifierActions.save(identifierData));
                });
                
                await Promise.all(identifierPromises);
            }

            const primaryNameRecord = names.find((name, index) => index === primaryName);
            if (isEditMode && primaryNameRecord && primaryNameRecord._isExisting) {
                const primaryNameId = primaryNameRecord.id;
                onSubmit({ id: primaryNameId, taxonomy: savedTaxonomy.value });
            } else {
                onSubmit(savedTaxonomy.value);
            }
        } catch (err) {
            setErrors({ submit: err.message });
        }
    };

    const handleFieldChange = (field, value) => {
        const updatedTaxonomy = new Taxonomy(taxonomy);
        updatedTaxonomy.set(field, value);
        setTaxonomy(updatedTaxonomy);
    };

    const makePrimary = (index) => {
        setPrimaryName(index);
    }

    const removeAlternateIdentifier = (index) => {
        const identifierToRemove = alternateIdentifiers[index];
        
        // If it's an existing identifier, add it to the delete list
        if (identifierToRemove._isExisting && identifierToRemove.id) {
            setDeletedIdentifierIds([...deletedIdentifierIds, identifierToRemove.id]);
        }
        
        // Remove from the current list
        const newIdentifiers = alternateIdentifiers.filter((_, i) => i !== index);
        
        // If we're removing the primary identifier, make the first remaining one primary
        if (primaryIdentifier === index && newIdentifiers.length > 0) {
            newIdentifiers[0].primary = true;
            setPrimaryIdentifier(0);
        } else if (primaryIdentifier > index) {
            setPrimaryIdentifier(primaryIdentifier - 1);
        }
        
        setAlternateIdentifiers(newIdentifiers);
    }

    const makePrimaryIdentifier = (index) => {
        const newIdentifiers = alternateIdentifiers.map((identifier, i) => ({
            ...identifier,
            primary: i === index
        }));
        setAlternateIdentifiers(newIdentifiers);
        setPrimaryIdentifier(index);
    }

    const removeName = (index) => {
        const nameToRemove = names[index];
        
        // If it's an existing name, add it to the delete list
        if (nameToRemove._isExisting && nameToRemove.id) {
            setDeletedNameIds([...deletedNameIds, nameToRemove.id]);
        }
        
        // Remove from the current list
        const newNames = names.filter((_, i) => i !== index);
        
        // If we're removing the primary name, make the first remaining one primary
        if (primaryName === index && newNames.length > 0) {
            setPrimaryName(0);
        } else if (primaryName > index) {
            setPrimaryName(primaryName - 1);
        }
        
        setNames(newNames);
    }

    const handleTaxonomyTypeChange = (value) => {
        const updatedTaxonomy = new Taxonomy(taxonomy);
        const taxonomyCategoryLabel = value.get('filteredFor')
        const taxonomyCategory = options.taxonomyCategories.find(tc => tc.get('label') === taxonomyCategoryLabel)
        updatedTaxonomy.set('taxonomyType', value);
        updatedTaxonomy.set('taxonomyCategory', taxonomyCategory);
        setTaxonomy(updatedTaxonomy);
    };



    const scientificBinomialForm = () => {
        return (
            <>
                <LookupTableInput
                    id="subject"
                    label="Subject"
                    value={taxonomy.get('subject')}
                    onChange={(value) => handleFieldChange('subject', value)}
                    options={options.subjects}
                    getOptionLabel={(option) => `${option?.get('label')} (${option?.get('shortCode')})`}
                    error={errors.subject}
                    required
                />

                <LookupTableInput
                    id="securityType"
                    label="Security Type"
                    value={taxonomy.get('securityType')}
                    onChange={(value) => handleFieldChange('securityType', value)}
                    options={options.securityTypes}
                    getOptionLabel={(option) => option?.get('label')}
                    error={errors.securityType}
                    required
                />

                <LookupTableInput
                    id="nativityType"
                    label="Nativity Type"
                    value={taxonomy.get('nativityType')}
                    onChange={(value) => handleFieldChange('nativityType', value)}
                    options={options.nativityTypes}
                    getOptionLabel={(option) => option?.get('label')}
                    error={errors.nativityType}
                    required
                />

                <LookupTableInput
                    id="invasiveStatus"
                    label="Invasive Status"
                    value={taxonomy.get('invasiveStatus')}
                    onChange={(value) => handleFieldChange('invasiveStatus', value)}
                    options={options.invasiveStatuses}
                    getOptionLabel={(option) => option?.get('label')}
                    error={errors.invasiveStatus}
                    required
                />

                <LookupTableInput
                    id="reproduction"
                    label="Reproduction"
                    value={taxonomy.get('reproduction')}
                    onChange={(value) => handleFieldChange('reproduction', value)}
                    options={options.reproductionTypes}
                    getOptionLabel={(option) => option?.get('label')}
                    error={errors.reproduction}
                    required
                />

                <LookupTableInput
                    id="rinhsStatus"
                    label="RINHS Status"
                    value={taxonomy.get('rinhsStatus')}
                    onChange={(value) => handleFieldChange('rinhsStatus', value)}
                    options={options.protectedStatuses}
                    getOptionLabel={(option) => option?.get('label')}
                    error={errors.rinhsStatus}
                    required
                />

                <LookupTableInput
                    id="sRank"
                    label="S-Rank"
                    value={taxonomy.get('sRank')}
                    onChange={(value) => handleFieldChange('sRank', value)}
                    options={options.sRanks}
                    getOptionLabel={(option) => option?.get('label')}
                    error={errors.sRank}
                    required
                />

                <LookupTableInput
                    id="rigcn"
                    label="RIGCN"
                    value={taxonomy.get('rigcn')}
                    onChange={(value) => handleFieldChange('rigcn', value)}
                    options={options.rigcns}
                    getOptionLabel={(option) => option?.get('label')}
                    error={errors.rigcn}
                    required
                />

                <LookupTableInput
                    id="protectedStatus"
                    label="Protected Status"
                    value={taxonomy.get('protectedStatus')}
                    onChange={(value) => handleFieldChange('protectedStatus', value)}
                    options={options.protectedStatuses}
                    getOptionLabel={(option) => option?.get('label')}
                    error={errors.protectedStatus}
                    required
                />
            </>
        )
    }

    const sortedTaxonomyTypes = options.taxonomyTypes.sort((a, b) => (
        b.get('order') - a.get('order')
    ));

    const filteredParentOptions = taxonomies.filter(t => {
        if (!taxonomy.get('taxonomyType')) return true
        const optionLevel = t.get('taxonomyType')?.get('order')
        const taxonomyLevel = taxonomy.get('taxonomyType')?.get('order')
        return optionLevel > taxonomyLevel

    });


    const alternateIdentifiersForm = () => {
        return (
            <>
                <Typography variant="h5">Alternate Identifiers</Typography>
                {alternateIdentifiers.map((identifier, index) => (
                    <Grid container spacing={2} key={index} alignItems="flex-start">
                        <Grid item xs={5}>
                            <LookupTableInput
                                id={`taxonomySystem-${index}`}
                                label="Taxonomy System"
                                value={identifier.taxonomySystem}
                                onChange={(value) => {
                                    const newIdentifiers = [...alternateIdentifiers];
                                    newIdentifiers[index].taxonomySystem = value;
                                    setAlternateIdentifiers(newIdentifiers);
                                }}
                                options={options.taxonomySystems}
                                getOptionLabel={(option) => option?.get('label')}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Input
                                label="Identifier"
                                value={identifier.identifier}
                                onChange={(value) => {
                                    const newIdentifiers = [...alternateIdentifiers];
                                    newIdentifiers[index].identifier = value;
                                    setAlternateIdentifiers(newIdentifiers);
                                }}
                            />
                        </Grid>
                        <Grid item xs={1}>
                            <Stack direction="row" alignItems="center" sx={{ mt: 3 }}>
                                <IconButton onClick={() => makePrimaryIdentifier(index)}>
                                    {primaryIdentifier === index ? <StarIcon /> : <StarBorderIcon />}
                                </IconButton>
                                <IconButton onClick={() => removeAlternateIdentifier(index)}>
                                    <DeleteIcon />
                                </IconButton>
                            </Stack>
                        </Grid>
                    </Grid>
                ))}
                
                <Button 
                    onClick={() => setAlternateIdentifiers([...alternateIdentifiers, { taxonomySystem: null, identifier: '' }])} 
                    variant="outlined"
                >
                    Add Another Identifier
                </Button>
            </>
        );
    };

    return (
        <form onSubmit={handleSubmit}>
            <Stack spacing={4}>
                {errors.submit && (
                    <Typography style={{color: 'red'}}>{errors.submit}</Typography>
                )}

                <LookupTableInput
                    id="taxonomyType"
                    label="Taxonomy Type"
                    value={taxonomy.get('taxonomyType')}
                    onChange={(value) => handleTaxonomyTypeChange(value)}
                    options={sortedTaxonomyTypes}
                    getOptionLabel={(option) => option?.get('label')}
                    error={errors.taxonomyType}
                />
                
                <LookupTableInput
                    id="taxonomyCategory"
                    label="Taxonomy Category"
                    value={taxonomy.get('taxonomyCategory')}
                    onChange={(value) => handleFieldChange('taxonomyCategory', value)}
                    options={options.taxonomyCategories}
                    getOptionLabel={(option) => option?.get('label')}
                    error={errors.taxonomyCategory}
                    disabled={!taxonomy.get('taxonomyType')}
                    required
                />
                
                {taxonomy.get('taxonomyCategory') && (
                    <LookupTableInput
                        id="parent"
                        label="Parent"
                        value={taxonomy.get('parent')}
                        onChange={(value) => handleFieldChange('parent', value)}
                        options={filteredParentOptions}
                        getOptionLabel={(option) => option?.get('primaryName')}
                        error={errors.taxonomyType}
                    />
                )}

                {taxonomy.get('taxonomyCategory')?.get('label') === 'Scientific Binomial' && scientificBinomialForm()}
                
                <Typography variant="h5">Names</Typography>
                {names.map((name, index) => (
                    <Grid container spacing={2} key={index}>
                        <Grid item xs={3}>
                            <Select
                                label="Type"
                                value={name.typeId}
                                onChange={(value) => {
                                    const newNames = [...names];
                                    newNames[index].typeId = value;
                                    setNames(newNames);
                                }}
                                options={['scientific', 'common']}
                                labels={{
                                    scientific: 'Scientific',
                                    common: 'Common'
                                }}
                                required
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Input
                                label="Name"
                                value={name.name}
                                onChange={(value) => {
                                    const newNames = [...names];
                                    newNames[index].name = value;
                                    setNames(newNames);
                                }}
                                required
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Stack direction="row" alignItems="center" sx={{ mt: 3 }}>
                                <Typography sx={{ ml: 2 }} variant="body2">Primary</Typography>
                                <IconButton onClick={() => makePrimary(index)}>
                                    {primaryName === index ? <StarIcon /> : <StarBorderIcon />}
                                </IconButton>
                                <IconButton 
                                    onClick={() => removeName(index)}
                                    disabled={names.length === 1 || primaryName === index}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Stack>
                        </Grid>
                    </Grid>
                ))}
                
                <Button 
                    onClick={() => setNames([...names, { typeId: 'common', name: '' }])} 
                    variant="outlined"
                >
                    Add Another Name
                </Button>
                
                {alternateIdentifiersForm()}
                <Button type="submit" disabled={!taxonomy.isSavable()}>
                    {initialValues?.taxonomy ? 'Update' : 'Create'} Taxonomy
                </Button>
            </Stack>
        </form>
    );
};

TaxonomyForm.propTypes = {
    options: PropTypes.shape({
        taxonomySystems: PropTypes.array.isRequired,
        subjects: PropTypes.array.isRequired,
        reproductionTypes: PropTypes.array.isRequired,
        nativityTypes: PropTypes.array.isRequired,
        invasiveStatuses: PropTypes.array.isRequired,
        securityTypes: PropTypes.array.isRequired,
        protectedStatuses: PropTypes.array.isRequired,
        taxonomyTypes: PropTypes.array.isRequired,
        taxonomyCategories: PropTypes.array.isRequired,
        sRanks: PropTypes.array.isRequired,
        rigcns: PropTypes.array.isRequired,
        keywords: PropTypes.array.isRequired,
    }).isRequired,
    onSubmit: PropTypes.func.isRequired,
    taxonomy: PropTypes.instanceOf(Taxonomy),
    taxonomies: PropTypes.array,
    initialValues: PropTypes.shape({
        taxonomy: PropTypes.object,
        names: PropTypes.array,
        alternateIdentifiers: PropTypes.array
    })
};

export default TaxonomyForm;